import useBusinessTravellerItemPolicyBudgetFetcher from 'businessTraveller/hooks/useBusinessTravellerItemPolicyBudgetFetcher'
import useQueryParams from 'hooks/useQueryParams'
import { getNumberOfNights, toIsoDateString } from 'lib/datetime/dateUtils'
import {
  getCheckInDateFromURLSearchParams,
  getCheckOutDateFromURLSearchParams,
  getOccupanciesFromURLSearchParams,
} from 'lib/url/searchUrlUtils'
import { Moment } from 'moment'
import { useMemo } from 'react'

interface Options {
  checkInDate?: Moment
  checkOutDate?: Moment
  roomCount?: number
  offer?: App.AnyOffer
  placeId?: string
  lat?: number
  lng?: number
}

/** The hotel search budget for the current traveller(s), from the item policy evaluation */
function useBusinessTravellerSearchBudgetFetcher(options: Options = {}) {
  const { offer, placeId, lat, lng } = options
  const queryParams = useQueryParams()

  const checkInDate = toIsoDateString(options.checkInDate ?? getCheckInDateFromURLSearchParams(queryParams))
  const checkOutDate = toIsoDateString(options.checkOutDate ?? getCheckOutDateFromURLSearchParams(queryParams))
  const roomCount = options.roomCount ?? getOccupanciesFromURLSearchParams(queryParams)?.length ?? 1

  const numberOfNights = useMemo(
    () => (checkInDate && checkOutDate ? getNumberOfNights(checkInDate, checkOutDate) : undefined),
    [checkInDate, checkOutDate],
  )

  const { budgetView, itemPolicyData, isEnabled, cacheKey, employeeIds } = useBusinessTravellerItemPolicyBudgetFetcher(offer, {
    placeId,
    lat,
    lng,
    numberOfNights,
    roomCount,
  })

  const hasBudget = !!budgetView?.isShown && budgetView.perRoomAmount > 0

  return {
    perRoomBudget: hasBudget ? budgetView.perRoomAmount : undefined,
    nightlyPerRoomBudget: hasBudget ? budgetView.perNightPerRoomAmount : undefined,
    numberOfNights,
    roomCount,
    isPending: isEnabled && (itemPolicyData === undefined || itemPolicyData.status === 'pending'),
    budgetView,
    cacheKey,
    employeeIds,
  }
}

export default useBusinessTravellerSearchBudgetFetcher
